import type {
  MLMachineSummary,
  MLProcessSummary,
  MLToolSummary,
  MLInferenceRequest,
  MLInferenceJobResult,
  MLInferenceResult,
} from '../type/MLTypes';
import type { BaseResponse, HttpClient } from '@/lib/http/HttpClient';

type MachineReadAllResponse = BaseResponse & {
  machines: Array<{
    machine: {
      id?: number;
      name: string;
      brand: string;
      model: string;
      kafkaName?: string;
    };
  }>;
};

type MLRegressionInferenceResponse = BaseResponse & {
  prediction: number;
  predictionUnit: string;
  predictionMinutes: number;
  features: Record<string, number>;
  warning?: string;
  partId?: string;
  passCount?: number;
  signalsStart?: string;
  signalsEnd?: string;
  volumeRemoved?: number;
};

export async function mlListMachines(http?: HttpClient): Promise<MLMachineSummary[]> {
  if (!http) throw new Error('ApiClient not initialized');

  const response = await http.post<MachineReadAllResponse>(
    '/api/tracabilite/machine/readAll',
    {}
  );

  if (!response.ok) {
    throw new Error(response.message ?? 'Failed to fetch machines');
  }

  return response.data.machines
    .map(({ machine }) => machine)
    .filter(
      (m): m is { id: number; name: string; brand: string; model: string; kafkaName?: string } =>
        m.id !== undefined
    )
    .map((m) => ({ id: m.id, name: m.name, brand: m.brand, model: m.model, kafkaName: m.kafkaName }));
}

type ProcessReadAllResponse = BaseResponse & {
  processes: Array<{
    process: {
      id?: number;
      name: string;
      toolReference: string;
      processType: 'standard' | 'test';
      archived: boolean;
      machineIds: number[];
      ae: number | null;
      ap: number | null;
      vc: number | null;
      fz: number | null;
    };
  }>;
};

type ToolReadAllResponse = BaseResponse & {
  tools: Array<{
    toolId: string;
    reference: string | null;
    traces: Array<{
      toolState: { toolState: string };
      createdAt: string | null;
    }>;
  }>;
};

export async function mlListProcesses(machineId: number, http?: HttpClient): Promise<MLProcessSummary[]> {
  if (!http) throw new Error('ApiClient not initialized');

  const response = await http.post<ProcessReadAllResponse>(
    '/api/tracabilite/process/readAll',
    {}
  );

  if (!response.ok) {
    throw new Error(response.message ?? 'Failed to fetch processes');
  }

  return response.data.processes
    .map(({ process: p }) => p)
    .filter((p) => p.id !== undefined && !p.archived && p.machineIds.includes(machineId))
    .map((p) => ({
      id: p.id!,
      name: p.name,
      toolReference: p.toolReference,
      processType: p.processType,
      ae: p.ae,
      ap: p.ap,
      vc: p.vc,
      fz: p.fz,
    }));
}

type RawTool = ToolReadAllResponse['tools'][number];

function mapTool(tool: RawTool): MLToolSummary {
  const sorted = [...tool.traces].sort((a, b) => {
    if (!a.createdAt && !b.createdAt) return 0;
    if (!a.createdAt) return 1;
    if (!b.createdAt) return -1;
    return b.createdAt.localeCompare(a.createdAt);
  });
  const latest = sorted[0];
  return {
    toolId: tool.toolId,
    reference: tool.reference,
    lastUsedAt: latest?.createdAt ?? null,
    status: (latest?.toolState?.toolState === 'worn' ? 'scrapped' : 'idle') as MLToolSummary['status'],
  };
}

async function fetchAllTools(http?: HttpClient): Promise<RawTool[]> {
  if (!http) throw new Error('ApiClient not initialized');

  const response = await http.post<ToolReadAllResponse>('/api/tracabilite/tool/readAll', {});

  if (!response.ok) {
    throw new Error(response.message ?? 'Failed to fetch tools');
  }

  return response.data.tools;
}

export async function mlListToolsForProcess(
  _processId: number,
  toolReference: string,
  http?: HttpClient
): Promise<MLToolSummary[]> {
  const tools = await fetchAllTools(http);
  return tools.filter((tool) => tool.reference === toolReference).map(mapTool);
}

export async function mlSubmitInference(
  req: MLInferenceRequest,
  http?: HttpClient
): Promise<{
  jobId: string;
  status: 'completed';
  result: MLInferenceResult;
  message?: string;
}> {
  if (!http) {
    throw new Error('ApiClient not initialized');
  }

  const response = await http.post<MLRegressionInferenceResponse>(
    '/api/machine-learning/predict/regression-xgb-full',
    {
      machineId: req.machineId,
      toolId: req.toolId,
      nbDents: req.nbDents,
      ap: req.ap,
      ae: req.ae,
      vc: req.vc,
      fz: req.fz,
      diameterCut: req.diameterCut,
      // Display context persisted with the server-side run record.
      ...(req.machine !== undefined ? { machine: req.machine } : {}),
      ...(req.processId !== undefined ? { processId: req.processId } : {}),
      ...(req.processName !== undefined ? { processName: req.processName } : {}),
    }
  );

  if (!response.ok) {
    const err = new Error(response.message ?? 'Machine learning inference failed');
    err.name = response.name;
    throw err;
  }

  return {
    jobId: `ml-${Date.now()}`,
    status: 'completed',
    result: {
      rulSeconds: response.data.prediction,
      ...(response.data.warning ? { warning: response.data.warning } : {}),
      window: {
        partId: response.data.partId ?? null,
        passCount: response.data.passCount ?? null,
        start: response.data.signalsStart ?? null,
        end: response.data.signalsEnd ?? null,
        volumeRemoved: response.data.volumeRemoved ?? null,
      },
    },
  };
}

// mlSubmitInference on the real path always wraps the synchronous backend
// response in { status: 'completed' }, so useMLInferenceJob never enters the
// polling branch and this function is never called on the real path.
// If the backend is ever made asynchronous (returns a real jobId without an
// inline result), this must be implemented to call the ML job-status endpoint.
export function mlGetInferenceStatus(_jobId: string): Promise<MLInferenceJobResult> {
  throw new Error('mlGetInferenceStatus: not implemented — see MLApiClient.real.ts');
}
